import React, {useEffect, useState} from 'react';
import {ScreenProps} from '../../../interfaces/screens/mainScreen';
import MainLayout from '../../mainLayout';
import GenericButton from '../../../components/button';
import styled, {useTheme} from 'styled-components/native';
import {tr} from '../../../translations';
import TextGeneric from '../../../components/textGeneric';
import TextInput from '../../../components/textInput';
import {Theme} from '../../../theme/lightTheme';
import {Platform, StatusBar} from 'react-native';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import screens from '../../../config/screens';
import {logIn} from './actions';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {checkIfFormValid, handleChange} from '../../../utils/form';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../../utils/responsive';
import Icon from '../../../components/icon';
import {
  finishLoading,
  startLoading,
} from '../../../connected-components/loading/actions';

// Define the mandatory fields for the form
const mandatoryFields = ['email', 'password'];

// Styled components
const Container = styled.View<{height: number; paddingTop: number}>`
  width: 100%;
  height: ${({height}) => height}px;
  padding-top: ${({paddingTop}) => paddingTop}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  flex: 1;
  align-items: center;
  justify-content: space-between;
`;

const InputContainer = styled(Animated.View)<{
  width: string;
  paddingTop: string;
  paddingHorizontal: string;
}>`
  flex: 1;
  padding-top: ${({paddingTop}) => paddingTop};
  padding-horizontal: ${({paddingHorizontal}) => paddingHorizontal};
  align-self: center;
  width: ${({width}) => width};
`;

const ImageContainer = styled(Animated.View)<{width: number}>`
  width: ${({width}) => width}px;
  aspect-ratio: 1.333;
  align-items: center;
  justify-content: center;
  align-self: center;
  overflow: hidden;
`;

const Image = styled.Image`
  width: 100%;
  height: 100%;
  ${Platform.OS !== 'web' && 'resize-mode: contain;'}
`;

const Title = styled(TextGeneric)<{
  color: string;
  fontSize: number;
  paddingBottom: number;
}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  text-align: center;
  padding-bottom: ${({paddingBottom}) => paddingBottom}px;
`;

const ForgetTextContainer = styled.TouchableOpacity`
  align-self: flex-end;
  padding-horizontal: 5%;
  margin-vertical: 3%;
`;

const ForgetText = styled(TextGeneric)<{color: string; fontSize: number}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  text-align: right;
`;

const SubmitButton = styled(GenericButton)<{
  width: number;
  marginTop: number;
  backgroundColor: string;
}>`
  margin-top: ${({marginTop}) => marginTop}px;
  width: ${({width}) => width}px;
  border-radius: 100px;
  align-self: center;
  background-color: ${({backgroundColor}) => backgroundColor};
`;

const LinkText = styled.TouchableOpacity`
  color: ${({theme}) => theme.text.dark};
  font-weight: bold;
  font-size: ${({theme}) => theme.text.s7}px;
  text-align: center;
  padding-top: 5%;
  padding-bottom: 5%;
  text-decoration: underline;
  cursor: pointer;
`;

const MessageBanner = styled(Animated.View)<{
  backgroundColor: string;
  borderColor: string;
}>`
  width: 100%;
  background-color: ${({backgroundColor}) => backgroundColor};
  border-width: 1px;
  border-color: ${({borderColor}) => borderColor};
  border-radius: 8px;
  padding: ${({theme}) => theme.text.s8}px;
  margin-bottom: ${({theme}) => theme.text.s8}px;
  flex-direction: row;
  align-items: center;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 2;
`;

const MessageIconContainer = styled.View<{iconColor: string}>`
  margin-right: ${({theme}) => theme.text.s8}px;
`;

const MessageContainer = styled.View`
  flex: 1;
`;

const MessageTextWrapper = styled(TextGeneric)<{color: string; fontSize: number}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  line-height: ${({fontSize}) => fontSize * 1.4}px;
`;

const ResendLinkText = styled(TextGeneric)<{color: string; fontSize: number; disabled: boolean}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  text-decoration: underline;
  opacity: ${({disabled}) => (disabled ? 0.6 : 1)};
`;

const CloseButton = styled.TouchableOpacity`
  margin-left: ${({theme}) => theme.text.s8}px;
  padding: 4px;
`;

/**
 * Login screen component.
 *
 * @param {ScreenProps} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const Login: React.FC<ScreenProps> = ({navigation}) => {
  // Get the current theme
  const theme = useTheme() as Theme;
  const {bottom: bottomInset, top: topInset} = useSafeAreaInsets();
  const [form, updateForm] = useState<{email: string; password: string}>({
    email: '',
    password: '',
  });
  const [formComplete, setFormComplete] = useState(false);
  
  // Decode base64 string
  const decodeBase64 = (str: string): string => {
    try {
      // First decode URL encoding if present
      const urlDecoded = decodeURIComponent(str);
      // Then decode base64
      return atob(urlDecoded);
    } catch (error) {
      // If base64 decode fails, try direct URL decode
      try {
        return decodeURIComponent(str);
      } catch (e) {
        return str;
      }
    }
  };

  // Read URL params immediately on component initialization (before any effects)
  const getInitialVerificationMessage = (): {
    status: 'success' | 'error' | null;
    message: string;
    email: string;
  } => {
    if (!isWeb) return {
      status: null,
      message: '',
      email: '',
    };
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('status');
      const message = urlParams.get('message');
      const email = urlParams.get('email') || '';
      
      if (status && message) {
        // Decode base64 encoded status and message
        const decodedStatus = decodeBase64(status);
        const decodedMessage = decodeBase64(message);
        const decodedEmail = email ? decodeBase64(email) : '';
        
        // Keep the message exactly as backend sends it, only use status for styling
        return {
          status: decodedStatus.toLowerCase() === 'success' ? 'success' : 'error',
          message: decodedMessage, // Keep message intact after decoding
          email: decodedEmail,
        };
      }
    } catch (error) {
    }
    return {status: null, message: '', email: ''};
  };
  
  const [verificationMessage, setVerificationMessage] = useState<{
    status: 'success' | 'error' | null;
    message: string;
    email: string;
  }>(getInitialVerificationMessage());
  const [showMessage, setShowMessage] = useState(
    verificationMessage.status !== null
  );
  const [isResending, setIsResending] = useState(false);
  
  // Use the responsive dimensions hook
  const {hdp, wdp, getByScreenSize, isLargeScreen} = useResponsiveDimensions();

  useEffect(() => {
    setFormComplete(checkIfFormValid(form, mandatoryFields));
  }, [form]);

  // Handle email verification redirect parameters and clean URL
  useEffect(() => {
    if (!isWeb || !showMessage || !verificationMessage.status) return;
    
    // Clean URL params after component has rendered and message is visible
    const cleanUrlTimer = setTimeout(() => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const hasStatus = urlParams.get('status');
        const hasMessage = urlParams.get('message');
        const hasEmail = urlParams.get('email');
        
        if (hasStatus || hasMessage || hasEmail) {
          urlParams.delete('status');
          urlParams.delete('message');
          urlParams.delete('email');
          const newSearch = urlParams.toString();
          const newUrl =
            window.location.pathname + (newSearch ? `?${newSearch}` : '');
          window.history.replaceState({}, document.title, newUrl);
        }
      } catch (error) {
        // Silently fail if URL manipulation fails
      }
    }, 500); // Delay to ensure component has fully rendered
    
    return () => {
      clearTimeout(cleanUrlTimer);
    };
  }, [showMessage, verificationMessage.status]);

  const submit = () => logIn({login: form?.email, password: form.password});
  const registerPressHandler = () =>
    navigation?.navigate(screens.auth.register);

  // Function to handle "Forgot Password" button press
  const forgetPressHandler = () =>
    navigation?.navigate(screens.auth.forgetPassword);

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!verificationMessage.email || isResending) return;
    
    setIsResending(true);
    startLoading();
    
    try {
      // Call the API directly to avoid showing toast
      const {services} = await import('../../../api');
      const res = await services.auth.resendVerification(verificationMessage.email);
      
      if (res?.content?.message) {
        // Update message to success with the API response message
        setVerificationMessage({
          status: 'success',
          message: res.content.message,
          email: verificationMessage.email,
        });
      } else {
        // Fallback message
        setVerificationMessage({
          status: 'success',
          message: tr('register.verificationEmailSent', 'Verification email sent successfully.'),
          email: verificationMessage.email,
        });
      }
    } catch (error) {
    } finally {
      setIsResending(false);
      finishLoading();
    }
  };

  // Calculate responsive values
  const containerHeight =
    hdp(100) - ((StatusBar.currentHeight || 0) + Math.max(bottomInset, hdp(2)));

  // Calculate responsive sizes
  const imageWidth = wdp(getByScreenSize(45, 13.5, 40));
  const inputContainerWidth = `${isLargeScreen ? 40 : 100}%`;
  const inputContainerPaddingHorizontal = isLargeScreen ? '0%' : '5%';
  const submitButtonWidth = wdp(isLargeScreen ? 20 : 90);
  const paddingTopPercentage = `${getByScreenSize(20, 2)}%`;

  return (
    <MainLayout
      isCustomHeader
      showOverlay={false}
      noPaddingHeader
      keyboardAvoidScrollView>
      <Container
        height={containerHeight}
        paddingTop={Math.max(topInset, hdp(5))}>
        <Title
          color={theme.text.dark}
          fontSize={theme.text.s4}
          paddingBottom={hdp(5)}>
          {tr('login.screenTitle')}
        </Title>
        <ContentContainer>
          <ImageContainer entering={FadeIn} width={imageWidth}>
            <Image
              resizeMode={'contain'}
              source={require('../../../assets/images/onboarding/onboard-1.png')}
            />
          </ImageContainer>
          <InputContainer
            entering={FadeIn}
            width={inputContainerWidth}
            paddingHorizontal={inputContainerPaddingHorizontal}
            paddingTop={paddingTopPercentage}>
            {showMessage && verificationMessage.status && verificationMessage.message && (
              <MessageBanner
                entering={FadeIn}
                exiting={FadeOut}
                backgroundColor={
                  verificationMessage.status === 'success'
                    ? theme.primary + '15'
                    : '#FFEBEE'
                }
                borderColor={
                  verificationMessage.status === 'success'
                    ? theme.primary
                    : '#F44336'
                }>
                <MessageIconContainer iconColor={
                  verificationMessage.status === 'success'
                    ? theme.primary
                    : '#F44336'
                }>
                  <Icon
                    name={verificationMessage.status === 'success' ? 'check' : 'error'}
                    size={theme.text.s6}
                    color={
                      verificationMessage.status === 'success'
                        ? theme.primary
                        : '#F44336'
                    }
                  />
                </MessageIconContainer>
                <MessageContainer>
                  <MessageTextWrapper
                    color={
                      verificationMessage.status === 'success'
                        ? theme.text.dark
                        : '#C62828'
                    }
                    fontSize={theme.text.s8}>
                    {verificationMessage.message}
                    {verificationMessage.status === 'error' && 
                     verificationMessage.email && (
                      <>
                        {' '}
                        <ResendLinkText
                          color="#C62828"
                          fontSize={theme.text.s8}
                          disabled={isResending}
                          onPress={isResending ? undefined : handleResendVerification}>
                          {isResending
                            ? tr('register.resending', 'Resending...')
                            : tr('register.resendVerification', 'Resend Verification Email')}
                        </ResendLinkText>
                      </>
                    )}
                  </MessageTextWrapper>
                </MessageContainer>
                <CloseButton onPress={() => setShowMessage(false)}>
                  <Icon
                    name="close"
                    size={theme.text.s7}
                    color={theme.text.title}
                  />
                </CloseButton>
              </MessageBanner>
            )}
            <TextInput
              label={tr('login.emailLabel')}
              placeholder={tr('login.email')}
              value={form.email}
              onChangeText={text => handleChange('email', text, updateForm)}
              keyboardType={'email-address'}
              autoCapitalize={'none'}
              autoCorrect={false}
              showLeftIcon
              leftIconName={'user'}
              leftIconColor={theme.primary}
            />
            <TextInput
              label={tr('login.passwordLabel')}
              placeholder={tr('login.password')}
              value={form.password}
              onChangeText={text => handleChange('password', text, updateForm)}
              secureInput
              containerStyle={{marginTop: hdp(2)}}
              showLeftIcon
              leftIconName={'lock'}
              leftIconColor={theme.secondary}
            />
            <ForgetTextContainer onPress={forgetPressHandler}>
              <ForgetText color={theme.primary} fontSize={theme.text.s9}>
                {tr('login.forget')}
              </ForgetText>
            </ForgetTextContainer>

            <SubmitButton
              width={submitButtonWidth}
              marginTop={getByScreenSize(0, hdp(5))}
              backgroundColor={!formComplete ? theme.text.light : theme.primary}
              label={tr('login.signIn')}
              radius={100}
              onPress={submit}
              disabled={!formComplete}
              style={{
                marginTop: 'auto',
              }}
              labelStyle={{
                color: !formComplete ? theme.secondary : theme.text.light,
              }}
            />
            <TextGeneric
              style={{
                color: theme.text.dark,
                fontSize: theme.text.s8,
                alignSelf: 'center',
                paddingTop: theme.text.s55,
              }}>
              {tr('login.registerPart1')}{' '}
              <LinkText onPress={registerPressHandler}>
                {tr('login.registerPart2')}
              </LinkText>
            </TextGeneric>
          </InputContainer>
        </ContentContainer>
      </Container>
    </MainLayout>
  );
};

export default Login;
